import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { OrganizzazioniService } from './organizzazioni.service';
import { firstValueFrom } from 'rxjs';
import { SearchResult } from '../_models/SearchResult';
import {
  Attivita,
  CambiaStatoRequest,
  Pianifica,
  PianificaFilterRequest,
  PianificazioneStatistiche,
  RiordinaAttivitaRequest,
  StatoGeo,
  TipoAttivita
} from '../_models/Pianificazione';
import { OrientamentoStampa } from '../_models/pianificazione/report-config.model';
import { OrdineVendita } from '../_models/OrdineVendita';

@Injectable({ providedIn: 'root' })
export class PianificazioneService {
  private get baseUrl(): string {
    return `${environment.localApiUrl}planning/`;
  }

  constructor(
    private http: HttpClient,
    private organizzazioniService: OrganizzazioniService
  ) {}

  private get orgId(): number {
    return this.organizzazioniService.organizzazioneId ?? 0;
  }

  // Pianifiche
  getPianifiche(request: PianificaFilterRequest): Promise<SearchResult<Pianifica>> {
    return firstValueFrom(
      this.http.post<SearchResult<Pianifica>>(`${this.baseUrl}Pianifica/PianificaFiltered`, request)
    );
  }

  getPianificaById(pianificaId: number): Promise<Pianifica> {
    return firstValueFrom(
      this.http.get<Pianifica>(`${this.baseUrl}Pianifica/PianificaFromId`, {
        params: { orgId: this.orgId, pianificaId }
      })
    );
  }

  // FR-016: sync — il backend ritorna il DTO con pianificaId>0 popolato dall'identity DB.
  // L'FE consuma direttamente il response per applicare l'ID ai placeholder e procedere col loop AddAttivita.
  createPianifica(dto: Partial<Pianifica>): Promise<Pianifica> {
    return firstValueFrom(
      this.http.post<Pianifica>(`${this.baseUrl}Pianifica/CreatePianifica`, { ...dto, organizzazioneId: this.orgId })
    );
  }

  updatePianifica(dto: Pianifica): Promise<void> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}Pianifica/UpdatePianifica`, dto)
    ).then(() => undefined);
  }

  cambiaStato(request: CambiaStatoRequest): Promise<void> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}Pianifica/CambiaStato`, request)
    ).then(() => undefined);
  }

  deletePianifica(pianificaId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete(`${this.baseUrl}Pianifica/DeletePianifica`, {
        params: { orgId: this.orgId, pianificaId }
      })
    ).then(() => undefined);
  }

  // Feature 081 (issue #3005): mappatura condivisa OdV → Attività "Ordine Cliente", riusata sia
  // dall'aggiunta manuale in pianificazione-detail sia dal re-link dopo un cambio data in
  // ordine-vendita-create (DRY, R-006). `posizione` è derivata dal chiamante (append in coda alla
  // pianifica destinazione); `dataCreazione` è impostata server-side da AddAttivita (il valore qui
  // serve solo all'uso locale della detail); `notaJson` è lo snapshot dell'ordine (feature 071).
  buildAttivitaFromOrdine(ordine: OrdineVendita, pianificaId: number, posizione: number): Attivita {
    return {
      attivitaId: 0,
      pianificaId,
      organizzazioneId: this.orgId,
      tipoAttivita: TipoAttivita.OrdineCliente,
      posizione,
      idDocumento: ordine.idOrdineVendita,
      idRiga: ordine.idRiga,
      rifOrdine: ordine.rifOrdine,
      citta: ordine.citta,
      cap: ordine.cap,
      provincia: ordine.provincia,
      nazione: ordine.nazione,
      dataConsegna: ordine.dataConsegnaRiga,
      statoGeo: StatoGeo.NonGeocodificata,
      dataCreazione: new Date(),
      notaJson: JSON.stringify(ordine)
    };
  }

  // Attività
  addAttivita(dto: Partial<Attivita>): Promise<void> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}Pianificazione/AddAttivita`, { ...dto, orgId: this.orgId })
    ).then(() => undefined);
  }

  updateAttivita(dto: Attivita): Promise<void> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}Pianificazione/UpdateAttivita`, dto)
    ).then(() => undefined);
  }

  riordinaAttivita(request: RiordinaAttivitaRequest): Promise<void> {
    return firstValueFrom(
      this.http.post(`${this.baseUrl}Pianificazione/RiordinaAttivita`, request)
    ).then(() => undefined);
  }

  deleteAttivita(attivitaId: number): Promise<void> {
    return firstValueFrom(
      this.http.delete(`${this.baseUrl}Pianificazione/DeleteAttivita`, {
        params: { orgId: this.orgId, attivitaId }
      })
    ).then(() => undefined);
  }

  // Feature 032 (T035, FR-014): orientamento aggiunto al payload (default Orizzontale per safety).
  stampaPDF(
    orgId: number,
    pianificaIds: number[],
    columns: string[],
    includiAttivitaLibere: boolean,
    orientamento: OrientamentoStampa = 'Orizzontale'
  ): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        `${this.baseUrl}Pianifica/ReportPDF`,
        { orgId, pianificaIds, columns, includiAttivitaLibere, orientamento },
        { responseType: 'blob' }
      )
    );
  }

  // Feature 062 (#2919, FR-001/FR-002): export Excel del report pianifiche. Stesso body di
  // stampaPDF ma senza orientamento (ignorato per l'Excel).
  stampaExcel(
    orgId: number,
    pianificaIds: number[],
    columns: string[],
    includiAttivitaLibere: boolean
  ): Promise<Blob> {
    return firstValueFrom(
      this.http.post(
        `${this.baseUrl}Pianifica/ReportExcel`,
        { orgId, pianificaIds, columns, includiAttivitaLibere },
        { responseType: 'blob' }
      )
    );
  }

  // Dashboard
  getStatistiche(dataDal?: Date, dataAl?: Date): Promise<PianificazioneStatistiche> {
    const params: any = { orgId: this.orgId };
    if (dataDal) params['dataDal'] = dataDal.toISOString();
    if (dataAl) params['dataAl'] = dataAl.toISOString();
    return firstValueFrom(
      this.http.get<PianificazioneStatistiche>(`${this.baseUrl}Pianificazione/Statistiche`, { params })
    );
  }
}
